import { useMsal } from "@azure/msal-react";
import { useMutation } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";
import { refusalDetail } from "./useEventPublish";
import { EventReview } from "../types/event";

const EVENT = "event/admin/";

/**
 * Check a saved event for clashes and gaps: `POST event/admin/{id}/review/`.
 *
 * A MUTATION, not a query. One review is a dozen database reads plus an AI
 * call, and the API allows 10 a minute per user, so it runs only when someone
 * presses the button: never on mount, never on a keystroke, never on a
 * refetch because the window regained focus.
 *
 * ⚠️ The result is held in memory only. It carries counts about named groups
 * of students; nothing here logs it or writes it to storage.
 */
/** What the editor holds and hands to both places that show the check. */
export type EventReviewState = ReturnType<typeof useEventReview>;

export function useEventReview(eventId: string) {
	const { instance } = useMsal();

	const review = useMutation<EventReview, unknown, string | undefined>({
		mutationFn: (question) =>
			callPymbleApi(instance, "POST", `${EVENT}${eventId}/review/`, {
				data: question ? { Question: question } : {},
			}),
	});

	return {
		/* `onDone` is per run, so the check that follows a save can react to
		   its own result without a manual rerun doing the same. */
		run: (question?: string, onDone?: (result: EventReview) => void) =>
			review.mutate(question?.trim() || undefined, { onSuccess: onDone }),
		result: review.data ?? null,
		running: review.isPending,
		/* A 429 comes back as "Too many event reviews". refusalDetail passes
		   only 4xx strings, so no server internals reach the screen. */
		error: review.isError
			? (refusalDetail(review.error) ??
				"Could not check this event. Try again.")
			: null,
		reset: review.reset,
	};
}
